import { useState } from 'react';

import { useMutation } from '@apollo/client';
import { Button } from '@mui/material';
import { convertsCentsToCurrency } from '@npi/buddy-utils';

import { Flex } from 'src/components/Flex';
import ConfirmationModal from 'src/components/Modals/ConfirmationModal';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { CANCEL_MERCHANT_APPLICATION } from 'src/graphql/queries/merchantApplications.query';
import { formatDate } from 'src/utils/utils';

import ModalTable from './ModalTable';

interface MerchantData {
    merchantInformation?: {
        aboutBusiness?: {
            legalCompanyName?: string;
            tradingName?: string;
        };
    };
    tradingName?: string;
    service?: {
        expiryDate?: string;
    };
    serviceRequirements?: {
        productOrder?: {
            primaryProducts?: Product[];
            secondaryProducts?: Product[];
            term?: number;
        };
    };
}

interface Product {
    description: string;
    monthlyPrice?: number;
    quantity?: number;
    id: number;
}

interface Props {
    isButtonEnabled: boolean;
    isSubmitting: boolean;
    applicationId: string;
    selectedType: string;
    validateApplicationId: () => Promise<boolean>;
    loading: boolean;
    validationError: string | null;
    merchantData: MerchantData;
    notificationId: string;
}

const CancelMerchantAction = ({
    isButtonEnabled,
    isSubmitting,
    applicationId,
    validateApplicationId,
    loading,
    validationError,
    merchantData,
    notificationId,
    selectedType,
}: Props) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [cancelApplication, { loading: mutationLoading }] = useMutation(
        CANCEL_MERCHANT_APPLICATION
    );

    const handleOpenModal = async () => {
        if (!applicationId) {
            showErrorToast('ID is required.');
            return;
        }

        try {
            const isValid = await validateApplicationId();
            if (isValid && !validationError) {
                setIsModalOpen(true);
            } else {
                showErrorToast(validationError ?? 'No data found for the provided ID.');
            }
        } catch (error) {
            showErrorToast('An unexpected error occurred.');
        }
    };

    const handleCancelService = async () => {
        try {
            const { data } = await cancelApplication({
                variables: { applicationIds: [parseInt(applicationId, 10)] },
            });

            if (data.cancelMerchantApplication) {
                showSuccessToast(
                    `${
                        selectedType === 'service' ? 'Service' : 'Application'
                    } with ID ${notificationId} has been cancelled.`
                );
                setIsModalOpen(false);
            } else {
                showErrorToast('Failed to cancel the merchant application.');
            }
        } catch (error) {
            showErrorToast(error.message);
        }
    };

    const legalCompanyName =
        merchantData?.merchantInformation?.aboutBusiness?.legalCompanyName ?? 'Unknown';
    const tradingName = merchantData?.tradingName ?? 'Unknown';
    const expiryDate = merchantData?.service?.expiryDate;
    const formattedExpiryDate = expiryDate ? formatDate({ date: new Date(expiryDate) }) : null;

    const modalMessage = `Are you sure you want to cancel ${legalCompanyName} T/A ${tradingName}${
        formattedExpiryDate ? ` service expiring on ${formattedExpiryDate}` : ''
    }?`;

    const products = [
        ...(merchantData?.serviceRequirements?.productOrder?.primaryProducts ?? []),
        ...(merchantData?.serviceRequirements?.productOrder?.secondaryProducts ?? []),
    ].map((product) => {
        const monthlyPrice = product.monthlyPrice ?? 0;
        const totalPrice =
            monthlyPrice *
            (product.quantity ?? 1) *
            merchantData?.serviceRequirements?.productOrder?.term;

        return {
            id: product.id,
            name: product.description,
            quantity: product.quantity ?? 0,
            price: `€${convertsCentsToCurrency(totalPrice).toFixed(2)}`,
        };
    });
    const getButtonText = () => {
        if (loading) return 'Validating...';
        if (isSubmitting) return 'Processing...';
        return 'Cancel Merchant’s Service';
    };

    return (
        <>
            <Flex justifyContent="flex-end">
                <Button
                    sx={{
                        alignSelf: 'end',
                        marginTop: '24px',
                        whiteSpace: 'nowrap',
                        minWidth: '200px',
                        textAlign: 'center',
                    }}
                    type="button"
                    variant="main"
                    disabled={!isButtonEnabled || isSubmitting || loading || mutationLoading}
                    onClick={handleOpenModal}
                >
                    {getButtonText()}
                </Button>
            </Flex>

            <ConfirmationModal
                isOpen={isModalOpen}
                title="Cancel Merchant"
                modalWidth="600px"
                message={<ModalTable modalMessage={modalMessage} products={products} />}
                confirmText="Yes"
                cancelText="No"
                onConfirm={handleCancelService}
                onCancel={() => {
                    return setIsModalOpen(false);
                }}
            />
        </>
    );
};

export default CancelMerchantAction;
